import { useEffect, useState } from "react"
import axios from "axios"

const queue: {
    event_id: string
    correlation_id: string
    operation: string
    code: string
    summary: string
    stack: string
}[] = []
let sending = false
let installed = false
let runtimeEnabled = false
const enabled = () => runtimeEnabled
export function useQualityTracking() {
    const [active, setActive] = useState(runtimeEnabled)
    useEffect(() => {
        const update = () => setActive(runtimeEnabled)
        window.addEventListener("quality-config", update)
        return () => window.removeEventListener("quality-config", update)
    }, [])
    return active
}
const id = () => crypto.randomUUID()
export function reportQualityError(code: string, error: unknown, operation = "browser", correlationId?: string) {
    if (!enabled() || queue.length >= 20) return
    // Exception messages can contain customer content. Send the type and sanitized stack only.
    const name = error instanceof Error ? error.name : "Error"
    const stack =
        error instanceof Error
            ? (error.stack || "")
                  .split("\n")
                  .slice(1, 9)
                  .join("\n")
                  .replace(/https?:\/\/[^\s)]+/g, url => url.split("?")[0].split("#")[0])
            : ""
    queue.push({
        event_id: id(),
        correlation_id: correlationId || id(),
        operation: operation.slice(0, 160),
        code,
        summary: name,
        stack: stack.slice(0, 4000),
    })
    void flush()
}
async function flush() {
    if (sending || !queue.length) return
    sending = true
    try {
        while (queue.length) {
            const response = await fetch("/quality/events/anonymous", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(queue[0]),
                signal: AbortSignal.timeout(5000),
            })
            if (!response.ok) throw new Error("Telemetry temporarily unavailable")
            queue.shift()
        }
    } catch {
        // Bounded memory only; no customer diagnostics in browser persistent storage.
    } finally {
        sending = false
    }
}
export function installQualityTelemetry() {
    if (installed) return
    void fetch("/quality/config", { signal: AbortSignal.timeout(5000) })
        .then(response => (response.ok ? response.json() : { enabled: false }))
        .then(config => {
            runtimeEnabled = config.enabled === true
            window.dispatchEvent(new Event("quality-config"))
        })
        .catch(() => {})
    installed = true
    window.addEventListener("error", event => reportQualityError("unhandled_error", event.error))
    window.addEventListener("unhandledrejection", event => reportQualityError("unhandled_rejection", event.reason))
    window.addEventListener("online", () => void flush())
    window.setInterval(() => void flush(), 30000)
    axios.interceptors.request.use(config => {
        if (enabled() && !config.url?.includes("/quality")) config.headers.set("X-Request-ID", id())
        return config
    })
    axios.interceptors.response.use(
        response => response,
        error => {
            if (
                !error.config?.url?.includes("/quality") &&
                !axios.isCancel(error) &&
                (!error.response || error.response.status >= 500)
            ) {
                const operation = `${error.config?.method || "GET"} ${(error.config?.url || "/").split("?")[0].replace(/[a-f\d]{24}|[a-f\d-]{36}/gi, ":id")}`
                reportQualityError(
                    error.response ? "http_error" : "network_error",
                    error,
                    operation,
                    error.response?.headers?.["x-request-id"] || error.config?.headers?.get?.("X-Request-ID")
                )
            }
            return Promise.reject(error)
        }
    )
}
